import React, { useEffect, useMemo, useState } from "react";

import { ScheduleCatalogService } from "../../../../services/v2/scheduleCatalog.service";
import "./catalogoHorarios.css";
import "./ofertaAcademica.css";

const servicio = new ScheduleCatalogService();

/*
	Oferta Académica (Esteban, 2026-10-27): reemplaza el WappDocs de la oferta. Las asesoras le toman captura a la
	hoja o copian el texto para WhatsApp. Precio y duración salen del plan del curso, los horarios del catálogo (los
	que todavía aceptan alumnos) y el texto de cada diplomado lo editan los Super Admin. La matrícula es GRATIS
	siempre: aquí no existe un precio de matrícula, solo lo que se ahorra.
*/

// Triple certificación en todos los planteles (Esteban, 2026-11-01: «Triple en todos lados»): Secretaría de Educación de
// Honduras, Estados Unidos y México. GRATIS donde ya lo era (Honduras y Online, que es de Honduras). En México nunca se
// nombra Honduras (regla del sitio público): ahí dice «triple certificación internacional».
const CERTIFICACION_GRATIS = [2, 196];
// En México la matrícula se llama "Inscripción" (Esteban, 2026-10-28).
const INSCRIPCION = [1];
const NOTAS = "_NOTAS";
const TODOS = "todos";
// Al abrir no se muestra nada hasta escoger diplomado (Esteban, 2026-10-28: "tan solo se abre debería pedir elegir el curso").
const ELEGIR = "";
// Próximos inicios que salen en la hoja; los que se quitan con × dejan pasar al siguiente (como en el Catálogo de Horarios).
const MAX_HORARIOS = 4;

interface Horario { id: number; horario: string; inicio: string; inicioTexto: string; dias: number[]; minuto: number; bloque: string | null; mensualidad: number | null; meses: number | null }
interface Curso { plantel: number; cursoId: number; codigo: string; nombre: string; meses: number | null; mensualidad: number | null; bloques: string[]; horarios: Horario[] }
interface Texto { id: number; pais: number; codigos: string[]; titulo: string; resumen: string; detalle: string; orden: number; visible: boolean }
interface Plantel { id: number; nombre: string; nombreLargo: string; pais: number; moneda: string }
interface Datos { generado: string; puedeEditar: boolean; planteles: Plantel[]; cursos: Curso[]; textos: Texto[] }

type Linea = { tipo: "titulo" | "vineta" | "parrafo"; texto: string };

/** El texto guardado: "## " = subtítulo, "- " = viñeta, lo demás párrafo. */
function lineas(texto: string): Linea[] {
	return String(texto || "").split("\n").map((l) => l.trim()).filter((l) => l !== "").map((l) => {
		if (l.startsWith("## ")) {
			return { tipo: "titulo", texto: l.slice(3).trim() };
		}
		if (l.startsWith("- ")) {
			return { tipo: "vineta", texto: l.slice(2).trim() };
		}
		return { tipo: "parrafo", texto: l };
	});
}

function dinero(plantel: Plantel | undefined, monto: number | null): string {
	if (monto === null || monto === undefined) {
		return "";
	}
	const cifra = Number(monto).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
	return plantel?.moneda === "L" ? "L " + cifra : (plantel?.moneda || "$") + cifra;
}

const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

// Lo que se ahorra con la matrícula gratis, en la moneda del plantel (L 1,000 en Honduras, $1,000 en México).
const AHORRO_MATRICULA = 1000;

/** "hasta mañana 29 de Octubre" (la promo que dan es gratis hasta el día siguiente, mes con mayúscula) + "Ahorra
	L 1,000!" (Esteban, 2026-10-28: ponen el precio y dicen que es gratis hasta 5 mensajes después; el ahorro es su mejor argumento). */
function matriculaGratis(plantel: Plantel | undefined): { hasta: string; ahorro: string } {
	const hoy = new Date();
	const manana = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + 1);
	const mes = manana.toLocaleDateString("es-MX", { month: "long" });
	return {
		hasta : "hasta mañana " + manana.getDate() + " de " + cap(mes),
		ahorro: "Ahorra " + dinero(plantel, AHORRO_MATRICULA) + "!"
	};
}

function meses(n: number | null): string {
	return n === null || n === undefined ? "" : n === 1 ? "1 mes" : n + " meses";
}

/** Texto de un curso: el del país cuyo grupo de códigos incluye el del curso. */
function textoDe(textos: Texto[], pais: number, codigo: string): Texto | undefined {
	return textos.find((t) => t.pais === pais && t.codigos.includes(codigo));
}

/** Para WhatsApp: *negrita*, viñetas con ▫️ y subtítulos en negrita. */
function aWhatsApp(texto: string): string {
	return formatoWhatsApp(lineas(texto));
}

function formatoWhatsApp(ls: Linea[]): string {
	return ls.map((l) => l.tipo === "titulo" ? "\n*" + l.texto + "*" : l.tipo === "vineta" ? "▫️ " + l.texto : l.texto).join("\n").trim();
}

// WhatsApp no deja mandar más de 4096 caracteres (Esteban, 2026-10-28). Si el plan no cabe, se recortan desde el final
// los renglones de "Al graduarte podrás", luego la Metodología y, de último, el final del plan. La hoja queda completa.
const LIMITE_WHATSAPP = 4096;
const RECORTABLES = [/^al graduarte/i, /^metodolog/i];

function planQueCabe(detalle: string, espacio: number): string {
	const ls = lineas(detalle);
	const largo = () => formatoWhatsApp(ls).length;
	RECORTABLES.forEach((patron) => {
		while (largo() > espacio) {
			const i = ls.findIndex((l) => l.tipo === "titulo" && patron.test(l.texto));
			if (i < 0) {
				return;
			}
			let fin = i + 1;
			while (fin < ls.length && ls[fin].tipo !== "titulo") {
				fin++;
			}
			// Los renglones de la sección, del último hacia arriba; si se queda vacía, también su título.
			ls.splice(fin - 1 > i ? fin - 1 : i, 1);
			if (ls[i]?.tipo === "titulo" && patron.test(ls[i].texto) && (i + 1 >= ls.length || ls[i + 1].tipo === "titulo")) {
				ls.splice(i, 1);
			}
		}
	});
	while (largo() > espacio && ls.length > 0) {
		ls.pop();
	}
	return formatoWhatsApp(ls);
}

function copiar(texto: string): Promise<void> {
	if (navigator.clipboard && window.isSecureContext) {
		return navigator.clipboard.writeText(texto);
	}
	return new Promise((resolve, reject) => {
		const area = document.createElement("textarea");
		area.value = texto;
		area.style.position = "fixed";
		area.style.opacity = "0";
		document.body.appendChild(area);
		area.select();
		const ok = document.execCommand("copy");
		document.body.removeChild(area);
		ok ? resolve() : reject(new Error("copy"));
	});
}

/** Botón verde de WhatsApp con su logo (Esteban, 2026-10-27: "en varios lados para que sea obvio"). */
const BotonWhatsApp: React.FC<{ onClick: () => void; flotante?: boolean }> = ({ onClick, flotante }) => (
	<button type="button" className={"ofBtnWhatsApp" + (flotante ? " ofBtnWhatsAppFlotante" : "")} onClick={onClick} title="Copiar para WhatsApp">
		<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
			<path fill="#FFFFFF" d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2z" />
			<path fill="#25D366" d="M17.2 14.3c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.2-1.4-.8-.7-1.4-1.6-1.5-1.9-.2-.3 0-.4.1-.6l.4-.5c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.1c-.2-.5-.5-.5-.7-.5h-.6c-.2 0-.5.1-.7.3-.3.3-1 .9-1 2.3s1 2.7 1.2 2.9c.1.2 2 3.1 4.9 4.3 2.4 1 2.9.8 3.4.7.5 0 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3z" />
		</svg>
		<span>Copiar para WhatsApp</span>
	</button>
);

const Detalle: React.FC<{ texto: string }> = ({ texto }) => {
	const bloques: React.ReactNode[] = [];
	let vinetas: string[] = [];
	const cerrar = () => {
		if (vinetas.length > 0) {
			bloques.push(<ul key={"u" + bloques.length}>{vinetas.map((v, i) => <li key={i}>{v}</li>)}</ul>);
			vinetas = [];
		}
	};
	lineas(texto).forEach((l) => {
		if (l.tipo === "vineta") {
			vinetas.push(l.texto);
			return;
		}
		cerrar();
		bloques.push(l.tipo === "titulo" ? <h4 key={"t" + bloques.length}>{l.texto}</h4> : <p key={"p" + bloques.length}>{l.texto}</p>);
	});
	cerrar();
	return <div className="ofDetalle">{bloques}</div>;
};

interface Edicion { id?: number; pais: number; codigo?: string; titulo: string; resumen: string; detalle: string; visible: boolean }

const Editor: React.FC<{ inicial: Edicion; campusId: number; alGuardar: () => void; alCerrar: () => void }> = ({ inicial, campusId, alGuardar, alCerrar }) => {
	const [e, setE] = useState<Edicion>(inicial);
	const [guardando, setGuardando] = useState(false);
	const [error, setError] = useState("");
	const guardar = () => {
		setGuardando(true);
		setError("");
		servicio.guardarTextoOferta(campusId, e).then(() => alGuardar())
			.catch((err) => setError(err?.response?.data?.message || "No se pudo guardar."))
			.finally(() => setGuardando(false));
	};
	return (
		<div className="ofEditor">
			<label>Título<input value={e.titulo} onChange={(ev) => setE({ ...e, titulo: ev.target.value })} /></label>
			{inicial.codigo !== NOTAS && <label>Resumen<textarea rows={3} value={e.resumen} onChange={(ev) => setE({ ...e, resumen: ev.target.value })} /></label>}
			<label>Plan de estudios y detalles<textarea rows={16} value={e.detalle} onChange={(ev) => setE({ ...e, detalle: ev.target.value })} />
				<small>## Subtítulo · - Viñeta</small></label>
			<label className="ofCheck"><input type="checkbox" checked={e.visible} onChange={(ev) => setE({ ...e, visible: ev.target.checked })} /> Visible en la Oferta</label>
			{error !== "" && <p className="ofError">{error}</p>}
			<div className="ofEditorBotones">
				<button type="button" className="ofBtn ofBtnGhost" onClick={alCerrar} disabled={guardando}>Cancelar</button>
				<button type="button" className="ofBtn ofBtnPrimary" onClick={guardar} disabled={guardando || e.titulo.trim() === ""}>{guardando ? "Guardando…" : "Guardar"}</button>
			</div>
		</div>
	);
};

const OfertaAcademica: React.FC<{ campusId: number; logo: string }> = ({ campusId, logo }) => {
	const [datos, setDatos] = useState<Datos | null>(null);
	const [error, setError] = useState("");
	const [plantelId, setPlantelId] = useState<number | null>(null);
	const [vista, setVista] = useState<string>(ELEGIR);
	const [conPlan, setConPlan] = useState(false);
	const [editando, setEditando] = useState<Edicion | null>(null);
	const [aviso, setAviso] = useState<{ ok: boolean; texto: string } | null>(null);
	const [menu, setMenu] = useState(false);
	const [limpia, setLimpia] = useState(false);
	const [busca, setBusca] = useState("");
	const [quitados, setQuitados] = useState<number[]>([]);

	/*
		El plantel de la barra de WAPP manda, con su moneda (Esteban, 2026-10-28: en Monterrey salían lempiras porque
		se quedaba el plantel visto antes). Otro plantel solo si se escoge a propósito con «◀ Plantel».
	*/
	const cargar = (deNuevo: boolean) => servicio.oferta(campusId).then((r) => {
		setDatos(r.data);
		setPlantelId((actual) => {
			const ids = (r.data.planteles || []).map((p: Plantel) => p.id);
			if (!deNuevo && actual !== null && ids.includes(actual)) {
				return actual;
			}
			return ids.includes(campusId) ? campusId : ids.length === 1 ? ids[0] : null;
		});
	}).catch(() => setError("No se pudo abrir la oferta académica."));

	useEffect(() => {
		setDatos(null);
		setVista(ELEGIR);
		setQuitados([]);
		setEditando(null);
		cargar(true);
	}, [campusId]); // eslint-disable-line react-hooks/exhaustive-deps

	const plantel = datos?.planteles.find((p) => p.id === plantelId);
	const pais = plantel?.pais ?? 0;
	const editar = !!datos?.puedeEditar;

	// Diplomados del plantel: los que tienen texto visible o horarios abiertos, en el orden del texto.
	const cursos = useMemo(() => {
		if (!datos || !plantel) {
			return [] as { curso: Curso; texto?: Texto }[];
		}
		return datos.cursos.filter((c) => c.plantel === plantel.id).map((c) => ({ curso: c, texto: textoDe(datos.textos, pais, c.codigo) }))
			.filter((x) => (x.texto ? x.texto.visible || editar : x.curso.horarios.length > 0))
			.sort((a, b) => (a.texto?.orden ?? 5000) - (b.texto?.orden ?? 5000) || a.curso.nombre.localeCompare(b.curso.nombre, "es"));
	}, [datos, plantel, pais, editar]);

	const notas = datos?.textos.find((t) => t.pais === pais && t.codigos.includes(NOTAS));
	const elegido = cursos.find((x) => String(x.curso.cursoId) === vista);
	const certGratis = CERTIFICACION_GRATIS.includes(pais);
	const matricula = INSCRIPCION.includes(pais) ? "Inscripción" : "Matrícula";

	useEffect(() => {
		if (!menu) {
			return;
		}
		const cerrar = () => setMenu(false);
		document.addEventListener("click", cerrar);
		return () => document.removeEventListener("click", cerrar);
	}, [menu]);

	useEffect(() => {
		if (vista !== ELEGIR && vista !== TODOS && vista !== NOTAS && !elegido) {
			setVista(ELEGIR);
		}
	}, [plantelId, vista, elegido]);

	const avisar = (ok: boolean, texto: string) => {
		setAviso({ ok, texto });
		window.setTimeout(() => setAviso(null), 3500);
	};

	// Horario con un precio distinto al del diplomado (historial de precios: p. ej. los que arrancan antes de enero).
	const otroPrecio = (c: Curso, h: Horario) => h.mensualidad !== null && c.mensualidad !== null && Math.abs(h.mensualidad - c.mensualidad) > 0.004;

	const nombreDe = (x: { curso: Curso; texto?: Texto }) => x.texto?.titulo || "Diplomado de " + x.curso.nombre;

	// Los próximos inicios por fecha (en los diplomados por bloques, de cualquier bloque), sin los quitados.
	const proximos = (c: Curso): Horario[] => c.horarios.filter((h) => !quitados.includes(h.id))
		.sort((a, b) => a.inicio.localeCompare(b.inicio) || (a.dias?.[0] ?? 9) - (b.dias?.[0] ?? 9) || (a.minuto ?? 0) - (b.minuto ?? 0))
		.slice(0, MAX_HORARIOS);
	const quitadosDe = (c: Curso) => c.horarios.filter((h) => quitados.includes(h.id)).length;

	const textoCurso = (x: { curso: Curso; texto?: Texto }): string => {
		const c = x.curso;
		const mat = matriculaGratis(plantel);
		const partes: string[] = [
			"🎓 *" + nombreDe(x) + "*",
			"📍 " + (plantel?.nombreLargo || ""),
			"",
			"✅ *" + matricula + " GRATIS* " + mat.hasta + "! " + mat.ahorro,
			"💳 *Mensualidad:* " + dinero(plantel, c.mensualidad),
			"⏳ *Duración:* " + meses(c.meses)
		];
		partes.push(certGratis ? "🏅 *Triple certificación:* ¡GRATIS! (Honduras, Estados Unidos y México)" : "🏅 *Triple certificación internacional*");
		if (c.bloques.length > 0) {
			partes.push("🧩 *Incluye:* " + c.bloques.join(", "));
		}
		if (x.texto?.resumen) {
			partes.push("", x.texto.resumen);
		}
		const lista = proximos(c);
		partes.push("", "🗓️ *Próximos inicios:*");
		if (lista.length === 0) {
			partes.push("Pregúntame por los próximos horarios 😊");
		}
		lista.forEach((h) => {
			partes.push("▫️ " + (h.bloque ? "*" + h.bloque + "* · " : "") + h.horario + " — inicia " + h.inicioTexto + (otroPrecio(c, h) ? " · " + dinero(plantel, h.mensualidad) + " al mes" : ""));
		});
		if (conPlan && x.texto?.detalle) {
			const encabezado = ["", "📚 *Plan de estudios*", ""];
			const plan = planQueCabe(x.texto.detalle, LIMITE_WHATSAPP - (partes.concat(encabezado).join("\n").length));
			if (plan !== "") {
				partes.push("", "📚 *Plan de estudios*", plan);
			}
		}
		return partes.join("\n");
	};

	const textoGeneral = (): string => {
		const mat = matriculaGratis(plantel);
		const partes = ["🎓 *Oferta Académica*", "📍 " + (plantel?.nombreLargo || ""), "", "✅ *" + matricula + " GRATIS* " + mat.hasta + "! " + mat.ahorro];
		partes.push(certGratis ? "🏅 Triple certificación *GRATIS* (Honduras, Estados Unidos y México)" : "🏅 Triple certificación internacional");
		partes.push("");
		cursos.forEach((x) => {
			partes.push("▫️ *" + x.curso.nombre + "* — " + meses(x.curso.meses) + " · " + dinero(plantel, x.curso.mensualidad) + " al mes");
		});
		partes.push("", "Pregúntame por los horarios del que te interese 😊");
		return partes.join("\n");
	};

	const alCopiar = () => {
		const texto = vista === NOTAS && notas ? "📌 *" + notas.titulo + "*\n\n" + aWhatsApp(notas.detalle) : elegido ? textoCurso(elegido) : textoGeneral();
		copiar(texto).then(() => avisar(true, "Copiado. Pégalo en WhatsApp.")).catch(() => avisar(false, "No se pudo copiar. Selecciona el texto y cópialo a mano."));
	};

	const abrirEditor = () => {
		if (vista === NOTAS) {
			setEditando(notas ? { id: notas.id, pais, codigo: NOTAS, titulo: notas.titulo, resumen: "", detalle: notas.detalle, visible: notas.visible }
				: { pais, codigo: NOTAS, titulo: "Notas Importantes", resumen: "", detalle: "", visible: true });
			return;
		}
		if (!elegido) {
			return;
		}
		const t = elegido.texto;
		setEditando(t ? { id: t.id, pais, titulo: t.titulo, resumen: t.resumen, detalle: t.detalle, visible: t.visible }
			: { pais, codigo: elegido.curso.codigo, titulo: "Diplomado de " + elegido.curso.nombre, resumen: "", detalle: "", visible: true });
	};

	if (error !== "") {
		return <div className="waOferta"><p className="ofNota">{error}</p></div>;
	}
	if (!datos) {
		return <div className="waOferta"><p className="ofNota">Cargando oferta…</p></div>;
	}
	const raiz = "waCatalogoHorarios waOferta" + (limpia ? " wchLimpia ofLimpia" : "");
	if (datos.planteles.length < 1) {
		return <div className={raiz}><p className="ofNota">Tu rol no tiene planteles con oferta.</p></div>;
	}
	if (!plantel) {
		return (
			<div className={raiz}>
				<h1 className="wchStepTitle">¿Qué plantel?</h1>
				<p className="wchStepLead">Elige uno para ver su oferta académica.</p>
				<div className="wchCampusGrid">
					{datos.planteles.map((p) => {
						const n = datos.cursos.filter((c) => c.plantel === p.id && (textoDe(datos.textos, p.pais, c.codigo)?.visible || c.horarios.length > 0)).length;
						return (
							<button key={p.id} type="button" className="wchCampusCard" onClick={() => { setPlantelId(p.id); setVista(ELEGIR); setQuitados([]); }}>
								<span className="wchCampusName">{p.nombre}</span>
								<span className="wchCampusCount">{n === 1 ? "1 diplomado" : n + " diplomados"}</span>
							</button>
						);
					})}
				</div>
			</div>
		);
	}

	const botonPlantel = datos.planteles.length > 1 && (
		<button type="button" className="wchBtn wchBtnGhost ofOcultable" onClick={() => { setPlantelId(null); setEditando(null); setVista(ELEGIR); }}>◀ Plantel</button>
	);

	if (vista === ELEGIR) {
		return (
			<div className={raiz}>
				{botonPlantel && <div className="wchFilterBar">{botonPlantel}</div>}
				<h1 className="wchStepTitle">¿Qué Diplomado?</h1>
				<p className="wchStepLead">{plantel.nombreLargo}</p>
				{cursos.length === 0 && <p className="ofNota">Este plantel todavía no tiene diplomados en la oferta.</p>}
				<div className="wchCampusGrid">
					{cursos.map((x) => (
						<button key={x.curso.cursoId} type="button" className={"wchCampusCard" + (x.texto && !x.texto.visible ? " ofOpcionOculta" : "")} onClick={() => setVista(String(x.curso.cursoId))}>
							<span className="wchCampusName">{x.curso.nombre}</span>
							<span className="wchCampusCount">{[meses(x.curso.meses), x.curso.mensualidad !== null ? dinero(plantel, x.curso.mensualidad) + " al mes" : ""].filter((t) => t !== "").join(" · ")}</span>
						</button>
					))}
				</div>
				<div className="ofOtrasVistas">
					{cursos.length > 1 && <button type="button" className="wchBtn wchBtnGhost" onClick={() => setVista(TODOS)}>Todos los Diplomados</button>}
					{(notas?.visible || editar) && <button type="button" className="wchBtn wchBtnGhost" onClick={() => setVista(NOTAS)}>Notas Importantes</button>}
				</div>
			</div>
		);
	}

	const opciones: { valor: string; texto: string; oculto?: boolean }[] = [{ valor: TODOS, texto: "Todos los Diplomados" },
		...cursos.map((x) => ({ valor: String(x.curso.cursoId), texto: x.curso.nombre, oculto: !!x.texto && !x.texto.visible })),
		...(notas?.visible || editar ? [{ valor: NOTAS, texto: "Notas Importantes" }] : [])];
	const actual = opciones.find((o) => o.valor === vista) || opciones[0];
	const sinAcento = (t: string) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

	return (
		<div className={raiz}>
			<div className="wchFilterBar">
				{botonPlantel}
				<div className="wchDrop ofOcultable" onClick={(e) => e.stopPropagation()}>
					<button type="button" className={"wchDropBtn" + (vista !== TODOS ? " wchDropBtnOn" : "")} onClick={() => setMenu(!menu)}>Diplomado: {actual.texto} ▾</button>
					{menu && (
						<div className="wchDropPanel">
							<input type="search" className="wchDropFind" placeholder="Buscar…" value={busca} onChange={(e) => setBusca(e.target.value)} autoFocus />
							{opciones.filter((o) => busca.trim() === "" || sinAcento(o.texto).includes(sinAcento(busca.trim()))).map((o) => (
								<button key={o.valor} type="button" className={"wchDropItem ofOpcion" + (o.valor === vista ? " ofOpcionActiva" : "") + (o.oculto ? " ofOpcionOculta" : "")}
									onClick={() => { setVista(o.valor); setMenu(false); setBusca(""); setEditando(null); }}>{o.texto}</button>
							))}
						</div>
					)}
				</div>
				{elegido && elegido.texto?.detalle && (
					<label className="ofCheck ofOcultable"><input type="checkbox" checked={conPlan} onChange={(e) => setConPlan(e.target.checked)} /> Incluir plan de estudios</label>
				)}
				<span className="ofEspacio" />
				{elegido && quitadosDe(elegido.curso) > 0 && (
					<button type="button" className="wchBtn wchBtnGhost ofOcultable" onClick={() => setQuitados(quitados.filter((id) => !elegido.curso.horarios.some((h) => h.id === id)))}>
						{quitadosDe(elegido.curso) === 1 ? "Regresar 1 Quitado" : "Regresar " + quitadosDe(elegido.curso) + " Quitados"}
					</button>
				)}
				{editar && vista !== TODOS && editando === null && <button type="button" className="wchBtn wchBtnGhost ofOcultable" onClick={abrirEditor}>Editar Texto</button>}
				<span className="ofOcultable"><BotonWhatsApp onClick={alCopiar} /></span>
				<button type="button" className="wchBtn wchBtnGhost" onClick={() => setLimpia(!limpia)} title="Oculta los botones para tomar la captura">{limpia ? "Mostrar botones" : "Vista limpia"}</button>
			</div>
			{aviso && <p className={"ofAviso " + (aviso.ok ? "ofAvisoOk" : "ofAvisoMal")}>{aviso.texto}</p>}

			{editando && <Editor inicial={editando} campusId={campusId} alCerrar={() => setEditando(null)}
				alGuardar={() => { setEditando(null); cargar(false); avisar(true, "Texto guardado."); }} />}

			<article className="ofHoja">
				<header className="ofHojaCabeza">
					<img className="ofLogo" src={logo} alt="Washington Academy" />
					<div>
						<p className="ofSobre">Oferta Académica</p>
						<p className="ofPlantel">{plantel.nombreLargo}</p>
					</div>
				</header>

				{vista === TODOS && (
					<div className="ofCuerpo">
						<div className="ofSellos">
							<span className="ofSello">{matricula} GRATIS {matriculaGratis(plantel).hasta}! {matriculaGratis(plantel).ahorro}</span>
							<span className="ofSello">Triple Certificación{certGratis ? " GRATIS" : ""}</span>
						</div>
						<table className="ofTabla">
							<thead><tr><th>Diplomado</th><th>Duración</th><th>Mensualidad</th><th>Próximo inicio</th></tr></thead>
							<tbody>
								{cursos.map((x) => (
									<tr key={x.curso.cursoId}>
										<td className="ofTablaCurso">{x.curso.nombre}</td>
										<td>{meses(x.curso.meses)}</td>
										<td className="ofCifra">{dinero(plantel, x.curso.mensualidad)}</td>
										<td>{proximos(x.curso)[0]?.inicioTexto || "Próximamente"}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}

				{vista === NOTAS && notas && (
					<div className="ofCuerpo">
						<h2 className="ofTitulo">{notas.titulo}</h2>
						<Detalle texto={notas.detalle} />
					</div>
				)}

				{elegido && (
					<div className="ofCuerpo">
						<h2 className="ofTitulo">{nombreDe(elegido)}</h2>
						<div className="ofDatos">
							<div className="ofDato ofDatoFuerte"><div className="ofMatUno"><span>{matricula}</span><strong>GRATIS</strong></div><em><b>{cap(matriculaGratis(plantel).hasta)}!</b> <b>{matriculaGratis(plantel).ahorro}</b></em></div>
							<div className="ofDato"><span>Mensualidad</span><strong>{dinero(plantel, elegido.curso.mensualidad)}</strong></div>
							<div className="ofDato"><span>Duración</span><strong>{meses(elegido.curso.meses)}</strong></div>
							<div className="ofDato"><span>Triple Certificación</span><strong>{certGratis ? "GRATIS" : "Internacional"}</strong></div>
						</div>
						{elegido.curso.bloques.length > 0 && <p className="ofBloques">Incluye {elegido.curso.bloques.join(" · ")}. Empiezas con el que quieras.</p>}
						{elegido.texto?.resumen && <p className="ofResumen">{elegido.texto.resumen}</p>}

						<h3 className="ofSubtitulo">Próximos Inicios</h3>
						{proximos(elegido.curso).length === 0 && <p className="ofNota">Próximamente.</p>}
						{proximos(elegido.curso).length > 0 && (
							<ul className="ofHorarios">
								{proximos(elegido.curso).map((h) => (
									<li key={h.id} className="ofHorario">
										<span className="ofHorarioNombre">{h.bloque && <span className="ofBloqueTag">{h.bloque}</span>}{h.horario}</span>
										<span className="ofInicio">Inicia {h.inicioTexto}{otroPrecio(elegido.curso, h) ? " · " + dinero(plantel, h.mensualidad) + "/mes" : ""}</span>
										<button type="button" className="ofQuitar ofOcultable" title="Quitar este horario de la hoja" aria-label="Quitar este horario de la hoja"
											onClick={() => setQuitados([...quitados, h.id])}>×</button>
									</li>
								))}
							</ul>
						)}

						{conPlan && elegido.texto?.detalle && (
							<>
								<h3 className="ofSubtitulo">Plan de Estudios</h3>
								<Detalle texto={elegido.texto.detalle} />
							</>
						)}
					</div>
				)}

				<footer className="ofPie">Washington Academy · Actualizado {datos.generado}</footer>
			</article>
			<div className="ofDebajo ofOcultable"><BotonWhatsApp onClick={alCopiar} /></div>
			{!limpia && <BotonWhatsApp onClick={alCopiar} flotante />}
		</div>
	);
};

export default OfertaAcademica;
